"use client";

import React, { useState, useEffect } from "react";
import { Button } from "@relume_io/relume-ui";
import { useFormSubmission } from "../hooks/useFormSubmission";
import { BiCheckCircle, BiErrorCircle } from "react-icons/bi";
import { useSearchParams } from "react-router-dom";

export function ContactPage() {
  const { submitForm, loading, error } = useFormSubmission();
  const [serviceType, setServiceType] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    // Product specific fields
    currentAiSolution: "",
    // Consulting specific fields
    projectTimeline: "",
    industryFocus: "",
    deploymentPreference: "",
  });
  const searchParams = useSearchParams()[0];

  // Set initial service type from URL parameter
  useEffect(() => {
    const typeFromUrl = searchParams.get("type");
    if (typeFromUrl === "consulting" || typeFromUrl === "product") {
      setServiceType(typeFromUrl === "consulting" ? "Consulting" : "Product");
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Log form values before submission
      console.log('Form Values Before Submission:', {
        serviceType,
        ...formData
      });
      
      await submitForm({
        serviceType,
        ...formData
      });
      setSubmitSuccess(true);
      // Reset form
      setFormData({
        name: "",
        email: "",
        company: "",
        message: "",
        currentAiSolution: "",
        projectTimeline: "",
        industryFocus: "",
        deploymentPreference: "",
      });
      setServiceType("");
    } catch (err) {
      // Error will be handled by the hook and displayed in the UI
      console.error("Form submission error:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Reset success message after 5 seconds
  React.useEffect(() => {
    if (submitSuccess) {
      const timer = setTimeout(() => setSubmitSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [submitSuccess]);

  return (
    <section className="px-[5%] py-16 md:py-24 lg:py-28 bg-gray-900 min-h-screen">
      <div className="container mx-auto max-w-3xl">
        <div className="text-center mb-12">
          <p className="mb-3 font-semibold md:mb-4 text-blue-400">Contact Us</p>
          <h1 className="mb-5 text-4xl font-bold md:text-5xl lg:text-6xl text-white">
            Get in Touch
          </h1>
          <p className="text-gray-300 md:text-lg">
            Let us know how we can help with your AI journey
          </p>
        </div>

        {/* Success Message */}
        {submitSuccess && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-3 text-green-400">
            <BiCheckCircle className="text-xl" />
            <p>Thank you for your message! We'll get back to you soon.</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3 text-red-400">
            <BiErrorCircle className="text-xl" />
            <p>{error}</p>
          </div>
        )}

        <div className="bg-gray-800 rounded-lg p-8 md:p-12">
          <div className="mb-8">
            <p className="text-white mb-4 font-medium">I'm interested in:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setServiceType("Product")}
                type="button"
                className={`p-6 rounded-lg border-2 transition-all ${
                  serviceType === "Product"
                    ? "border-blue-400 bg-blue-400/10"
                    : "border-gray-700 hover:border-gray-600"
                }`}
              >
                <h3 className="text-lg font-semibold text-white mb-2">Omnigeist Platform</h3>
                <h2 className="text-sm font-bold bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text animate-pulse">
                  Early Access
                </h2>
                <p className="text-sm text-gray-300">
                  Get on the waitlist to try our Transmedia IP Development Platform
                </p>
              </button>
              <button
                onClick={() => setServiceType("Consulting")}
                type="button"
                className={`p-6 rounded-lg border-2 transition-all ${
                  serviceType === "Consulting"
                    ? "border-blue-400 bg-blue-400/10"
                    : "border-gray-700 hover:border-gray-600"
                }`}
              >
                <h3 className="text-lg font-semibold text-white mb-2">Consulting Services</h3>
                <p className="text-sm text-gray-300">
                  Get expert help with your AI projects
                </p>
              </button>
            </div>
          </div>

          {serviceType && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Common Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label 
                    htmlFor="name" 
                    className="block mb-2 text-sm font-medium text-white"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    placeholder="Enter your name"
                    required
                    disabled={loading}
                  />
                </div>
                <div>
                  <label 
                    htmlFor="email" 
                    className="block mb-2 text-sm font-medium text-white"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    placeholder="Enter your email"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label 
                  htmlFor="company" 
                  className="block mb-2 text-sm font-medium text-white"
                >
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  placeholder="Enter your company name"
                  disabled={loading}
                />
              </div>

              {/* Product Specific Fields */}
              {serviceType === "Product" && (
                <div>
                  <label 
                    htmlFor="currentAiSolution" 
                    className="block mb-2 text-sm font-medium text-white"
                  >
                    Current AI Solution
                  </label>
                  <input
                    type="text"
                    id="currentAiSolution"
                    name="currentAiSolution"
                    value={formData.currentAiSolution}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                    placeholder="What AI solutions are you currently using?"
                    disabled={loading}
                  />
                </div>
              )}

              {/* Consulting Specific Fields */}
              {serviceType === "Consulting" && (
                <>
                  <div>
                    <label 
                      htmlFor="projectTimeline" 
                      className="block mb-2 text-sm font-medium text-white"
                    >
                      Project Timeline
                    </label>
                    <select
                      id="projectTimeline"
                      name="projectTimeline"
                      value={formData.projectTimeline}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      disabled={loading}
                    >
                      <option value="">Select timeline</option>
                      <option value="Immediate">Immediate</option>
                      <option value="Short-term">Short-term</option>
                      <option value="Long-term">Long-term</option>
                    </select>
                  </div>
                  <div>
                    <label 
                      htmlFor="industryFocus" 
                      className="block mb-2 text-sm font-medium text-white"
                    >
                      Industry Focus
                    </label>
                    <input
                      type="text"
                      id="industryFocus"
                      name="industryFocus"
                      value={formData.industryFocus}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      placeholder="What industry are you in?"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label 
                      htmlFor="deploymentPreference" 
                      className="block mb-2 text-sm font-medium text-white"
                    >
                      Deployment Preference
                    </label>
                    <select
                      id="deploymentPreference"
                      name="deploymentPreference"
                      value={formData.deploymentPreference}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      disabled={loading}
                    >
                      <option value="">Select preference</option>
                      <option value="Cloud">Cloud</option>
                      <option value="On-Premise">On-Premise</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>
                </>
              )}

              <div>
                <label 
                  htmlFor="message" 
                  className="block mb-2 text-sm font-medium text-white"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  placeholder="How can we help you?"
                  required
                  disabled={loading}
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className={`w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Sending..." : "Send Message"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
